import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';

import { useSession } from '../../session';

import { Workspace, listWorkspaces, openWorkspace } from '../../api/workspaces';
import { inSetup, openFailureMessage, openWithWake, workspacePresentation } from '../../pages/Projects/WorkspacesPanel';

/**
 * The studio a signed-in person can open right now: running or sleeping (it wakes), and not held by a setup
 * job. Undefined when there is none, in which case the control points at the setup card instead.
 */
export function openableStudio(rows: Workspace[]): Workspace | undefined {
  return rows.find((row) => workspacePresentation(row.state).openable && !inSetup(row));
}

export type StudioLinkState = 'hidden' | 'open' | 'setup';

export function studioLinkState(rows: Workspace[] | null | undefined): StudioLinkState {
  if (rows === undefined || rows === null) return 'hidden';
  return openableStudio(rows) ? 'open' : 'setup';
}

/** The control itself, kept free of effects so it renders the same on the server and in tests. */
export function StudioLinkView({ state, busy = false, error = '', onOpen }: {
  state: StudioLinkState; busy?: boolean; error?: string; onOpen: () => void;
}) {
  if (state === 'hidden') return null;
  return (
    <span className="studio-link" data-testid="studio-link">
      {state === 'open' ? (
        <button type="button" className="studio-link-button" disabled={busy} onClick={onOpen}>
          {busy ? 'Opening…' : '← Open studio'}
        </button>
      ) : (
        <Link to="/projects" className="studio-link-setup">← Set up your studio</Link>
      )}
      {error ? <span role="alert" className="error studio-link-error">{error} <Link to="/support">Report a problem</Link></span> : null}
    </span>
  );
}

/**
 * Persistent "back to your studio" control for every signed-in front-door page (walkthrough F-66). It uses the
 * same hand-off as the Workspaces card's open button: a hand-off POST, never a plain link, so the studio
 * accepts the sign-in. Shown as "Open studio" only when the org has a studio it can open.
 */
export function StudioLink() {
  const [rows, setRows] = useState<Workspace[] | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const [location] = useLocation();
  const orgId = useSession().session?.org_id ?? '';
  // Another account never inherits the previous account's rows.
  useEffect(() => { setRows(undefined); setError(''); setBusy(false); }, [orgId]);
  // Read again on every page change, so a studio that finished setting up shows as soon as the person navigates.
  useEffect(() => {
    let current = true;
    alive.current = true;
    listWorkspaces()
      .then((value) => { if (current) setRows(value); })
      .catch(() => {
        // Not configured on this deployment, or the list is down: no control rather than a wrong one.
        if (current) setRows(null);
      });
    return () => { current = false; };
  }, [location, orgId]);
  useEffect(() => () => { alive.current = false; }, []);

  async function open() {
    const studio = rows ? openableStudio(rows) : undefined;
    if (!studio || busy) return;
    setBusy(true);
    setError('');
    try {
      await openWithWake(studio, {
        open: (row) => openWorkspace(row.workspace_id, row.ingress_host, undefined, () => alive.current),
        list: async () => {
          const next = await listWorkspaces();
          if (alive.current) setRows(next);
          return next;
        },
        onWaking: () => setError('Waking your studio… it opens by itself when ready.'),
        wait: (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
        now: () => Date.now(),
      });
    } catch (failure) {
      if (!alive.current) return;
      setError(openFailureMessage(failure));
      setBusy(false);
    }
  }

  return <StudioLinkView state={studioLinkState(rows)} busy={busy} error={error} onOpen={() => void open()} />;
}
